/**
 * Nova Derma — pure helpers shared by API and web.
 * No framework imports, no side effects.
 * Frozen file — do not edit during feature work.
 */

import {
  DEFAULT_REQUIRED_NAME_PARTS,
  MONEY_DECIMALS,
  QUANTITY_DECIMALS,
  SMS_GSM7_MULTI,
  SMS_GSM7_SINGLE,
  SMS_UCS2_MULTI,
  SMS_UCS2_SINGLE,
} from './constants';
import type { AgeBandDto } from './types';

// ---------------------------------------------------------------------------
// Money & quantities
// ---------------------------------------------------------------------------

/** Round to the currency's precision using half-up, which is what accountants expect. */
export function roundMoney(value: number): number {
  return roundTo(value, MONEY_DECIMALS);
}

export function roundQuantity(value: number): number {
  return roundTo(value, QUANTITY_DECIMALS);
}

export function roundTo(value: number, decimals: number): number {
  if (!Number.isFinite(value)) return 0;
  const factor = Math.pow(10, decimals);
  // Nudge by Number.EPSILON so 1.0005 does not fall to 1.000 through binary error.
  return Math.round((value + Number.EPSILON * Math.sign(value)) * factor) / factor;
}

/**
 * DS-11/DS-51 — amounts always render LTR with grouped thousands, even inside
 * Arabic text. We force `en-US` digits deliberately: the clinic reads Western
 * numerals on financial screens.
 */
export function formatMoney(value: number | string, withSymbol = false, lang: 'AR' | 'EN' = 'AR'): string {
  const n = typeof value === 'string' ? Number(value) : value;
  const safe = Number.isFinite(n) ? n : 0;
  const text = safe.toLocaleString('en-US', {
    minimumFractionDigits: MONEY_DECIMALS,
    maximumFractionDigits: MONEY_DECIMALS,
  });
  if (!withSymbol) return text;
  return lang === 'AR' ? `${text} د.أ` : `JD ${text}`;
}

/** Quantities drop trailing zeros — "2" reads better than "2.0000" in a grid. */
export function formatQuantity(value: number | string): string {
  const n = typeof value === 'string' ? Number(value) : value;
  const safe = Number.isFinite(n) ? n : 0;
  const rounded = roundQuantity(safe);
  return rounded
    .toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: QUANTITY_DECIMALS });
}

export function formatPercent(value: number | string, decimals = 1): string {
  const n = typeof value === 'string' ? Number(value) : value;
  const safe = Number.isFinite(n) ? n : 0;
  return `${roundTo(safe, decimals).toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals,
  })}%`;
}

/**
 * INV-72 / BIL-01 — a discount is either a percentage or a flat amount, and the
 * result is clamped so a line can never go negative.
 */
export function applyDiscount(
  gross: number,
  discountType: 'PERCENT' | 'AMOUNT' | null | undefined,
  discountValue: number | null | undefined,
): { discountAmount: number; net: number } {
  const base = Number.isFinite(gross) ? gross : 0;
  if (!discountType || !discountValue) return { discountAmount: 0, net: roundMoney(base) };
  const raw = discountType === 'PERCENT' ? (base * discountValue) / 100 : discountValue;
  const discountAmount = roundMoney(Math.min(Math.max(raw, 0), base));
  return { discountAmount, net: roundMoney(base - discountAmount) };
}

/**
 * INV-74 — the number that travels through the whole system.
 * consumption unit cost = (purchase unit price after discount) ÷ conversion factor
 */
export function consumptionUnitCost(purchaseUnitPriceAfterDiscount: number, conversionFactor: number): number {
  if (!conversionFactor || conversionFactor <= 0) return 0;
  return roundTo(purchaseUnitPriceAfterDiscount / conversionFactor, 6);
}

/**
 * COST-01 — the one commission formula for every procedure.
 * commission = (price after discount − direct material cost) × rate
 *
 * COST-10 — never negative: a zero-revenue retouch pays zero, not a clawback.
 * COST-08 — `collectionRatio` scales the commission to what was actually
 * collected, so a partial payment pays a partial commission.
 */
export function calculateCommission(params: {
  netRevenue: number;
  materialCost: number;
  ratePercent: number;
  collectionRatio?: number;
}): { profit: number; commission: number } {
  const profit = roundMoney(params.netRevenue - params.materialCost);
  const ratio = params.collectionRatio === undefined ? 1 : clamp(params.collectionRatio, 0, 1);
  const raw = (profit * params.ratePercent) / 100;
  const commission = roundMoney(Math.max(raw, 0) * ratio);
  return { profit, commission };
}

export function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

export function safeDivide(numerator: number, denominator: number): number {
  if (!denominator) return 0;
  return numerator / denominator;
}

// ---------------------------------------------------------------------------
// Age & age groups (MSG-10..15)
// ---------------------------------------------------------------------------

/**
 * MSG-12 — age is always derived, never stored, so the doctor can redefine the
 * bands and re-run last year's report without touching a single row.
 */
export function calculateAge(dateOfBirth: Date | string, asOf: Date | string = new Date()): number {
  const dob = typeof dateOfBirth === 'string' ? new Date(dateOfBirth) : dateOfBirth;
  const ref = typeof asOf === 'string' ? new Date(asOf) : asOf;
  if (Number.isNaN(dob.getTime()) || Number.isNaN(ref.getTime())) return 0;
  let age = ref.getFullYear() - dob.getFullYear();
  const monthDiff = ref.getMonth() - dob.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && ref.getDate() < dob.getDate())) age--;
  return Math.max(age, 0);
}

/** Resolve which band an age falls into. Returns null when nothing matches. */
export function resolveAgeBand(age: number, bands: AgeBandDto[]): AgeBandDto | null {
  return (
    bands.find((b) => age >= b.minAge && (b.maxAge === null || age <= b.maxAge)) ?? null
  );
}

/**
 * MSG-14 — reject a set that overlaps or leaves a gap before it can be saved.
 * Bands are checked in ascending order of `minAge`.
 */
export function validateAgeBands(bands: Pick<AgeBandDto, 'minAge' | 'maxAge'>[]): {
  valid: boolean;
  errors: string[];
} {
  const errors: string[] = [];
  if (bands.length === 0) return { valid: false, errors: ['المجموعة فارغة'] };

  const sorted = [...bands].sort((a, b) => a.minAge - b.minAge);

  for (const band of sorted) {
    if (band.maxAge !== null && band.maxAge < band.minAge) {
      errors.push(`الفئة ${band.minAge}–${band.maxAge}: الحد الأعلى أصغر من الحد الأدنى`);
    }
  }

  // Exactly one open-ended band is allowed, and it must be the last one.
  const openEnded = sorted.filter((b) => b.maxAge === null);
  if (openEnded.length > 1) errors.push('لا يسمح بأكثر من فئة مفتوحة النهاية');
  if (openEnded.length === 1 && sorted[sorted.length - 1].maxAge !== null) {
    errors.push('الفئة المفتوحة النهاية يجب أن تكون الأخيرة');
  }

  for (let i = 0; i < sorted.length - 1; i++) {
    const current = sorted[i];
    const next = sorted[i + 1];
    if (current.maxAge === null) continue;
    if (next.minAge <= current.maxAge) {
      errors.push(`تداخل بين ${current.minAge}–${current.maxAge} و ${next.minAge}–${next.maxAge ?? '+'}`);
    } else if (next.minAge > current.maxAge + 1) {
      errors.push(`فجوة بين ${current.maxAge} و ${next.minAge}`);
    }
  }

  return { valid: errors.length === 0, errors };
}

// ---------------------------------------------------------------------------
// SMS (MSG-06)
// ---------------------------------------------------------------------------

/** Characters representable in the 7-bit GSM alphabet. Anything else forces UCS-2. */
const GSM7_CHARSET =
  '@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !"#¤%&\'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà';
const GSM7_EXTENDED = '^{}\\[~]|€';

/**
 * Arabic text is always UCS-2, which caps a single SMS at 70 characters — the
 * counter has to say so before the clinic sends 3 000 of them.
 */
export function countSmsSegments(text: string): {
  characters: number;
  encoding: 'GSM7' | 'UCS2';
  segments: number;
  charsPerSegment: number;
  remainingInSegment: number;
} {
  const isGsm7 = [...text].every((ch) => GSM7_CHARSET.includes(ch) || GSM7_EXTENDED.includes(ch));
  // Extended GSM characters cost two septets each.
  const weighted = isGsm7
    ? [...text].reduce((sum, ch) => sum + (GSM7_EXTENDED.includes(ch) ? 2 : 1), 0)
    : [...text].length;

  const single = isGsm7 ? SMS_GSM7_SINGLE : SMS_UCS2_SINGLE;
  const multi = isGsm7 ? SMS_GSM7_MULTI : SMS_UCS2_MULTI;

  if (weighted === 0) {
    return { characters: 0, encoding: isGsm7 ? 'GSM7' : 'UCS2', segments: 0, charsPerSegment: single, remainingInSegment: single };
  }

  const segments = weighted <= single ? 1 : Math.ceil(weighted / multi);
  const charsPerSegment = segments === 1 ? single : multi;
  const remainingInSegment = segments * charsPerSegment - weighted;

  return {
    characters: weighted,
    encoding: isGsm7 ? 'GSM7' : 'UCS2',
    segments,
    charsPerSegment,
    remainingInSegment,
  };
}

/** MSG-05 — substitute {variable} placeholders; unknown ones are left untouched. */
export function renderTemplate(template: string, values: Record<string, string | number | null | undefined>): string {
  return template.replace(/\{(\w+)\}/g, (match, key: string) => {
    const value = values[key];
    return value === undefined || value === null ? match : String(value);
  });
}

// ---------------------------------------------------------------------------
// Names & text
// ---------------------------------------------------------------------------

/** PAT-01 — the name is stored in parts and joined for display. */
export function joinName(parts: {
  firstName?: string | null;
  fatherName?: string | null;
  grandfatherName?: string | null;
  familyName?: string | null;
}): string {
  return [parts.firstName, parts.fatherName, parts.grandfatherName, parts.familyName]
    .map((p) => (p ?? '').trim())
    .filter(Boolean)
    .join(' ');
}

// ---------------------------------------------------------------------------
// Quick registration & profile completeness (PAT-01, PAT-03)
// ---------------------------------------------------------------------------

/**
 * The name parts in the order the clinic writes them. `patient.requiredNameParts`
 * is a flag per position, so the clinic can make any one optional on its own
 * (the first is always mandatory).
 */
export const PATIENT_NAME_PARTS = [
  'firstName',
  'fatherName',
  'grandfatherName',
  'familyName',
] as const;

/**
 * PAT-01 — which of the four name parts every file must carry, indexed the same
 * way as `PATIENT_NAME_PARTS`. `firstName` is always mandatory: no create path
 * can open a file without it, so the normaliser forces its flag true.
 */
export type RequiredNameParts = boolean[];

/**
 * PAT-01 — read the clinic's per-part requirement in either shape it has ever
 * had: the legacy count ("the first N parts") or the per-part boolean array
 * introduced when a clinic needed e.g. the third name optional and the fourth
 * mandatory. Called wherever the setting is read, so a value written before the
 * change keeps resolving to the same answer.
 */
export function normaliseRequiredNameParts(value: unknown): RequiredNameParts {
  if (Array.isArray(value) && value.length === PATIENT_NAME_PARTS.length) {
    const flags = value.map((flag) => Boolean(flag));
    flags[0] = true; // الاسم الأول إجباري بكل مسارات الإنشاء
    return flags;
  }

  const count =
    typeof value === 'number' && Number.isFinite(value) ? Math.trunc(value) : null;
  const flags =
    count === null
      ? [...DEFAULT_REQUIRED_NAME_PARTS]
      : PATIENT_NAME_PARTS.map((_, index) => index < count);
  flags[0] = true;
  return flags;
}

/**
 * What a patient file may still be missing after a quick registration at the
 * booking desk. `firstName` and the phone are not on the list: no path in the
 * system can open a file without them, so they can never be the thing that is
 * missing.
 */
export type PatientProfileField =
  | 'fatherName'
  | 'grandfatherName'
  | 'familyName'
  | 'gender'
  | 'dateOfBirth';

export interface PatientProfileInput {
  fatherName?: string | null;
  grandfatherName?: string | null;
  familyName?: string | null;
  gender?: string | null;
  dateOfBirth?: Date | string | null;
}

/**
 * Named here rather than in the patients module because the appointment status
 * machine has to say *what* is missing when it refuses to start a session, and
 * that module deliberately does not depend on Patients. The screens translate
 * through i18n; these are for the API's own bilingual error bodies.
 */
export const PATIENT_PROFILE_FIELD_LABELS_AR: Record<PatientProfileField, string> = {
  fatherName: 'اسم الأب',
  grandfatherName: 'اسم الجد',
  familyName: 'اسم العائلة',
  gender: 'الجنس',
  dateOfBirth: 'تاريخ الميلاد',
};

export const PATIENT_PROFILE_FIELD_LABELS_EN: Record<PatientProfileField, string> = {
  fatherName: "father's name",
  grandfatherName: "grandfather's name",
  familyName: 'family name',
  gender: 'gender',
  dateOfBirth: 'date of birth',
};

/**
 * The one definition of "this file is not finished yet", shared by the API that
 * enforces it and the screens that explain it.
 *
 * A booking may be taken against a half-filled file — reception has a patient on
 * the phone and a slot to hold — but the clinic still needs a gender and a date
 * of birth before anything clinical happens, because MSG-12 derives every age
 * band from `dateOfBirth` and half the lab reference ranges are sexed. So the
 * gap is carried on the file and answered later, at the desk, rather than
 * blocking the booking or being invented at registration time.
 *
 * `requiredNameParts` is the clinic's own setting and is never hard-coded here;
 * passing it in is what keeps this function agreeing with `buildName()`. It
 * accepts either stored shape (a legacy count or the per-part flags) and is read
 * through `normaliseRequiredNameParts`, so a name part the clinic marked optional
 * is skipped while a later one it marked mandatory is still checked.
 */
export function missingPatientProfileFields(
  patient: PatientProfileInput,
  requiredNameParts: unknown,
): PatientProfileField[] {
  const flags = normaliseRequiredNameParts(requiredNameParts);
  const missing: PatientProfileField[] = [];

  PATIENT_NAME_PARTS.forEach((part, index) => {
    // Skipping index 0: `firstName` is required by every create path there is.
    if (index === 0 || !flags[index]) return;
    if (!(patient[part as keyof PatientProfileInput] as string | null | undefined)?.toString().trim()) {
      missing.push(part as PatientProfileField);
    }
  });

  if (!patient.gender) missing.push('gender');
  if (!patient.dateOfBirth) missing.push('dateOfBirth');

  return missing;
}

/**
 * Normalise Arabic for search and duplicate detection (PAT-07): strip
 * diacritics and tatweel, and fold the alef/ya/ta-marbuta variants that people
 * type interchangeably.
 */
export function normalizeArabic(input: string): string {
  return input
    .replace(/[ً-ٰٟ]/g, '') // harakat
    .replace(/ـ/g, '') // tatweel
    .replace(/[أإآٱ]/g, 'ا')
    .replace(/ى/g, 'ي')
    .replace(/ة/g, 'ه')
    .replace(/ؤ/g, 'و')
    .replace(/ئ/g, 'ي')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

/** Cheap similarity used by the "did you mean this patient?" prompt (PAT-07). */
export function similarityRatio(a: string, b: string): number {
  const s1 = normalizeArabic(a);
  const s2 = normalizeArabic(b);
  if (!s1 || !s2) return 0;
  if (s1 === s2) return 1;
  const distance = levenshtein(s1, s2);
  return 1 - distance / Math.max(s1.length, s2.length);
}

export function levenshtein(a: string, b: string): number {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;

  let previous = Array.from({ length: b.length + 1 }, (_, i) => i);
  let current = new Array<number>(b.length + 1);

  for (let i = 1; i <= a.length; i++) {
    current[0] = i;
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      current[j] = Math.min(current[j - 1] + 1, previous[j] + 1, previous[j - 1] + cost);
    }
    [previous, current] = [current, previous];
  }
  return previous[b.length];
}

// ---------------------------------------------------------------------------
// Misc
// ---------------------------------------------------------------------------

/** Deterministic slug for codes and keys. */
export function slugify(input: string): string {
  return input
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9؀-ۿ]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/** Group an array by a derived key, preserving insertion order. */
export function groupBy<T, K extends string | number>(items: T[], keyFn: (item: T) => K): Map<K, T[]> {
  const map = new Map<K, T[]>();
  for (const item of items) {
    const key = keyFn(item);
    const bucket = map.get(key);
    if (bucket) bucket.push(item);
    else map.set(key, [item]);
  }
  return map;
}

export function sumBy<T>(items: T[], valueFn: (item: T) => number): number {
  return items.reduce((total, item) => total + (valueFn(item) || 0), 0);
}

/** Contrast-aware text colour for a coloured chip (provider colours, tags). */
export function readableTextColor(hexBackground: string): '#ffffff' | '#0f172a' {
  const hex = hexBackground.replace('#', '');
  if (hex.length !== 6) return '#0f172a';
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  // Relative luminance, sRGB coefficients.
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.6 ? '#0f172a' : '#ffffff';
}
